import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { authApi } from '../services/authApi';
import { DeviceFingerprintService } from '../services/deviceFingerprint';
import { toastSuccess, toastError } from '../lib/toast';
import { preloadAuthenticatedRoutes } from '../utils/routePreloader';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  role: string;
  is_verified: boolean;
  mfa_enabled?: boolean;
  profile_picture_url?: string;
  last_login_at?: string;
  has_completed_setup?: boolean;
  onboarding_completed?: boolean;
}

interface SecurityInfo {
  device_id: string;
  is_new_device: boolean;
  requires_verification: boolean;
  trust_score: number;
  geographic_anomaly: boolean;
  last_login_location?: string;
}

interface OAuthTokens {
  accessToken: string;
  refreshToken: string;
  provider?: string;
}

interface AuthError {
  message: string;
  code?: 'INVALID_CREDENTIALS' | 'ACCOUNT_LOCKED' | 'SECURITY_PASSWORD_RESET_REQUIRED' | 'UNKNOWN';
  details?: {
    lockout_duration?: number;
    account_locked?: boolean;
    retry_after?: number;
    password_reset_required?: boolean;
    reset_reason?: string;
  };
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  tokenExpiresAt: number | null; // Timestamp when token expires
  isAuthenticated: boolean;
  isLoading: boolean;
  error: AuthError | null;
  securityInfo: SecurityInfo | null;

  // Auto-refresh timer (not persisted)
  refreshTimer: NodeJS.Timeout | null;

  // Actions
  login: (
    email: string,
    password: string,
    rememberDevice?: boolean
  ) => Promise<void>;
  loginWithOAuth: (tokens: OAuthTokens) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  fetchUserProfile: () => Promise<void>;
  setUser: (user: User) => void;
  setTokens: (
    accessToken: string,
    refreshToken: string,
    expiresIn: number
  ) => void;
  setError: (error: AuthError) => void;
  clearAuth: () => void;
  clearError: () => void;
  getAccessToken: () => string | null; // Helper to get current token
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      tokenExpiresAt: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      securityInfo: null,
      refreshTimer: null,

      login: async (
        email: string,
        password: string,
        rememberDevice = false
      ) => {
        set({ isLoading: true, error: null });

        try {
          // Generate device fingerprint
          const deviceFingerprint =
            await DeviceFingerprintService.generateFingerprint();

          // Get location if available
          const location = await DeviceFingerprintService.getLocationInfo();

          // Format device info for backend
          const deviceInfo = {
            screen: {
              width: deviceFingerprint.screenResolution.split('x')[0],
              height: deviceFingerprint.screenResolution.split('x')[1],
              colorDepth: deviceFingerprint.colorDepth,
            },
            timezone: deviceFingerprint.timezone,
            language: deviceFingerprint.language,
            platform: deviceFingerprint.platform,
            browser_info: {
              userAgent: deviceFingerprint.userAgent,
              language: deviceFingerprint.language,
              platform: deviceFingerprint.platform,
            },
            os_info: {
              platform: deviceFingerprint.platform,
            },
            screen_info: {
              width: parseInt(deviceFingerprint.screenResolution.split('x')[0]),
              height: parseInt(deviceFingerprint.screenResolution.split('x')[1]),
              colorDepth: deviceFingerprint.colorDepth,
            },
          };

          // Make login request
          const response = await authApi.login({
            email,
            password,
            device_info: deviceInfo,
            location:
              location &&
              location.latitude &&
              location.longitude &&
              location.accuracy
                ? {
                    latitude: location.latitude,
                    longitude: location.longitude,
                    accuracy: location.accuracy,
                  }
                : undefined,
            remember_device: rememberDevice,
          });

          const {
            user,
            access_token,
            refresh_token,
            expires_in,
            security_info,
          } = response;

          // Mark device as trusted if requested and login successful
          if (rememberDevice && security_info.device_id) {
            DeviceFingerprintService.markAsTrusted(security_info.device_id);
          }

          // Set tokens and start refresh timer
          get().setTokens(access_token, refresh_token, expires_in);


          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            securityInfo: security_info,
          });

          // Sync onboarding store with current user (clear if different user)
          const { useOnboardingStore } = await import('./onboardingStore');
          useOnboardingStore.getState().syncUserId(user.id);

          // Preload authenticated routes immediately after successful login
          preloadAuthenticatedRoutes();

          // Show success toast
          toastSuccess(`Welcome back${user.first_name ? `, ${user.first_name}` : ''}!`);
        } catch (apiError: any) {
          let authError: AuthError = {
            message: 'Login failed. Please try again.',
            code: 'UNKNOWN'
          };

          // Handle AuthError objects thrown directly from authApi
          if (apiError && typeof apiError === 'object') {
            // Check if it's an AuthError from our API client
            if (apiError.code && apiError.message) {
              authError = {
                message: apiError.message,
                code: apiError.code,
                details: apiError.details
              };
            } else if (apiError?.response?.data) {
              // Handle axios-style errors (legacy)
              const errorData = apiError.response.data;
              
              if (typeof errorData.detail === 'string') {
                authError.message = errorData.detail;
                // Try to determine error type from message content
                if (errorData.detail.toLowerCase().includes('locked')) {
                  authError.code = 'ACCOUNT_LOCKED';
                } else if (errorData.detail.toLowerCase().includes('invalid')) {
                  authError.code = 'INVALID_CREDENTIALS';
                }
              } else if (errorData.detail && typeof errorData.detail === 'object') {
                authError.message = errorData.detail.message || errorData.detail;
                authError.code = errorData.detail.error_code || 'UNKNOWN';
                authError.details = {
                  lockout_duration: errorData.detail.lockout_duration,
                  account_locked: errorData.detail.account_locked,
                  password_reset_required: errorData.detail.password_reset_required,
                  reset_reason: errorData.detail.reset_reason
                };
              }
            } else if (apiError?.detail) {
              // Fallback for legacy error format
              if (typeof apiError.detail === 'string') {
                authError.message = apiError.detail;
                // Try to determine error type from message content
                if (apiError.detail.toLowerCase().includes('locked')) {
                  authError.code = 'ACCOUNT_LOCKED';
                } else if (apiError.detail.toLowerCase().includes('invalid') || apiError.detail.toLowerCase().includes('password')) {
                  authError.code = 'INVALID_CREDENTIALS';
                }
              } else if (apiError.detail.message) {
                authError.message = apiError.detail.message;
                authError.details = {
                  lockout_duration: apiError.detail.lockout_duration,
                  account_locked: apiError.detail.account_locked,
                  retry_after: apiError.detail.retry_after
                };
                // Set code based on details
                if (apiError.detail.account_locked) {
                  authError.code = 'ACCOUNT_LOCKED';
                } else {
                  authError.code = 'INVALID_CREDENTIALS';
                }
              }
            }
          }

          set({
            isLoading: false,
            error: authError,
            isAuthenticated: false,
            user: null,
            accessToken: null,
            refreshToken: null,
            securityInfo: null,
          });
          throw apiError;
        }
      },

      logout: async () => {
        const { refreshToken: currentRefreshToken, refreshTimer } = get();

        try {
          // Clear refresh timer
          if (refreshTimer) {
            clearTimeout(refreshTimer);
          }

          // Call logout API if we have a refresh token
          if (currentRefreshToken) {
            await authApi.logout(currentRefreshToken);
          }
        } catch (error) {
          console.error('Logout API call failed:', error);
          // Continue with local logout even if API fails
        }

        // Clear all auth state
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          securityInfo: null,
          refreshTimer: null,
        });


        // Show logout success toast
        toastSuccess('Successfully logged out');
      },

      refreshAccessToken: async () => {
        const { refreshToken: currentRefreshToken } = get();

        if (!currentRefreshToken) {
          get().logout();
          return;
        }

        try {
          const response = await authApi.refreshToken(currentRefreshToken);
          get().setTokens(
            response.access_token,
            currentRefreshToken,
            response.expires_in
          );
        } catch (error) {
          console.error('Token refresh failed:', error);
          get().logout();
        }
      },

      fetchUserProfile: async () => {
        try {
          const profileData = await authApi.getProfile() as any;

          // Map API response to User interface
          const updatedUser: User = {
            ...get().user, // Keep existing data
            id: profileData.user_id || profileData.id,
            email: profileData.email,
            first_name: profileData.first_name || null,
            last_name: profileData.last_name || null,
            phone_number: profileData.phone_number || null,
            role: profileData.role,
            is_verified: profileData.is_verified || profileData.email_verified,
            last_login_at: profileData.last_login_at,
            onboarding_completed: profileData.onboarding_completed ?? get().user?.onboarding_completed,
          };

          set({ user: updatedUser });
        } catch (error) {
          console.error('Failed to fetch user profile:', error);
        }
      },

      setUser: (user: User) => {
        set({ user, isAuthenticated: true });
      },

      setTokens: (
        accessToken: string,
        refreshToken: string,
        expiresIn: number
      ) => {
        const { refreshTimer } = get();

        // Clear existing timer
        if (refreshTimer) {
          clearTimeout(refreshTimer);
        }

        // Calculate token expiry timestamp
        const expiresAt = Date.now() + (expiresIn * 1000);

        // Set tokens in store (single source of truth)
        set({
          accessToken,
          refreshToken,
          tokenExpiresAt: expiresAt // Store expiry time for hydration
        });

        // Schedule token refresh (refresh 1 minute before expiry)
        const refreshTime = Math.max((expiresIn - 60) * 1000, 30000); // At least 30 seconds
        const newTimer = setTimeout(() => {
          get().refreshAccessToken();
        }, refreshTime);

        set({ refreshTimer: newTimer });
      },

      clearAuth: () => {
        const { refreshTimer } = get();

        if (refreshTimer) {
          clearTimeout(refreshTimer);
        }

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          tokenExpiresAt: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          securityInfo: null,
          refreshTimer: null,
        });
      },

      getAccessToken: () => {
        return get().accessToken;
      },

      loginWithOAuth: async (tokens: OAuthTokens) => {
        set({ isLoading: true, error: null });

        try {
          // Set tokens first
          get().setTokens(tokens.accessToken, tokens.refreshToken, 1800);

          // Fetch user profile with the access token
          const userProfile = await authApi.getProfile();

          // Set authenticated state with user data
          set({
            user: {
              id: userProfile.id,
              email: userProfile.email,
              first_name: userProfile.first_name,
              last_name: userProfile.last_name,
              role: userProfile.role,
              is_verified: userProfile.email_verified,
              last_login_at: userProfile.last_login_at,
              onboarding_completed: userProfile.onboarding_completed,
            },
            isAuthenticated: true,
            isLoading: false,
          });

          // Sync onboarding store with current user (clear if different user)
          const { useOnboardingStore } = await import('./onboardingStore');
          useOnboardingStore.getState().syncUserId(userProfile.id);

          // Show success toast for OAuth login
          toastSuccess(`Welcome back${userProfile.first_name ? `, ${userProfile.first_name}` : ''}!`);
        } catch (error: any) {
          console.error('OAuth login failed:', error);

          const authError: AuthError = {
            message: 'OAuth login failed. Please try again.',
            code: 'UNKNOWN'
          };

          if (error?.detail) {
            if (typeof error.detail === 'string') {
              authError.message = error.detail;
            } else if (error.detail.message) {
              authError.message = error.detail.message;
            }
          }

          set({
            isLoading: false,
            error: authError,
            isAuthenticated: false,
            user: null,
            accessToken: null,
            refreshToken: null,
            securityInfo: null,
          });
          throw error;
        }
      },

      setError: (error: AuthError) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },

    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      // Only persist user, tokens, token expiry, and security info (NOT refreshTimer)
      partialize: state => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        tokenExpiresAt: state.tokenExpiresAt,
        isAuthenticated: state.isAuthenticated,
        securityInfo: state.securityInfo,
      }),
      // Restore refresh timer on rehydration
      onRehydrateStorage: () => state => {
        if (state?.accessToken && state?.refreshToken && state?.tokenExpiresAt) {
          // Calculate remaining time until expiry
          const now = Date.now();
          const expiresIn = Math.max(0, Math.floor((state.tokenExpiresAt - now) / 1000));

          if (import.meta.env.DEV) {
            console.log('[Auth] Rehydrated auth state:', {
              hasToken: !!state.accessToken,
              expiresIn: `${expiresIn}s`,
              isExpired: expiresIn <= 0
            });
          }

          // If token is expired or expires in < 5 minutes, refresh immediately
          if (expiresIn < 300) {
            if (import.meta.env.DEV) {
              console.log('[Auth] Token expired or expiring soon, refreshing...');
            }
            setTimeout(() => {
              state.refreshAccessToken?.();
            }, 100);
          } else {
            // Schedule refresh 1 minute before expiry
            const refreshTime = Math.max((expiresIn - 60) * 1000, 30000);
            if (import.meta.env.DEV) {
              console.log('[Auth] Scheduling token refresh in', Math.floor(refreshTime / 1000), 'seconds');
            }

            const newTimer = setTimeout(() => {
              state.refreshAccessToken?.();
            }, refreshTime);

            // Update timer in state (non-reactive, just for cleanup)
            if (state.refreshTimer) {
              clearTimeout(state.refreshTimer);
            }
            state.refreshTimer = newTimer;
          }
        }
      },
    }
  )
);
